/**
 * Watches apps the attorney is connecting in another tab (the provider's
 * OAuth) until each shows up `connected`. There is no event to await, so the
 * hook polls `GET /api/connectors/<slug>` every `POLL_INTERVAL_MS` for the
 * slugs still pending, and gives up after `POLL_MAX_MS`.
 *
 * Shared by the Connectors page's cards and the agent's connect card.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ConnectorsAPI } from "../api/connectorsApi";
import type { ConnectorStatus } from "../types";

export const POLL_INTERVAL_MS = 3000;
export const POLL_MAX_MS = 3 * 60 * 1000;

export type WatchedStatus = ConnectorStatus | "unknown";

type Options = {
  /** Called once every watched slug reports `connected`. */
  onAllConnected?: (statuses: Record<string, WatchedStatus>) => void;
  /** Called when the watch gives up before that. */
  onTimeout?: (statuses: Record<string, WatchedStatus>) => void;
};

export type ConnectionWatch = {
  /** Latest known status per slug (starts from what the caller passed). */
  statuses: Record<string, WatchedStatus>;
  /** Slugs being polled right now. */
  watching: readonly string[];
  /** Start (or restart) watching a slug the attorney just opened the link for. */
  watch: (slug: string) => void;
  /** Stop everything (unmount, skip). */
  stop: () => void;
  /** Whether the watch ran out of time with something still unconnected. */
  timedOut: boolean;
};

export function useConnectionWatch(
  initial: Record<string, WatchedStatus>,
  options: Options = {}
): ConnectionWatch {
  const [statuses, setStatuses] = useState(initial);
  const [watching, setWatching] = useState<string[]>([]);
  const [timedOut, setTimedOut] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAt = useRef(0);
  const mounted = useRef(true);
  const watchingRef = useRef<string[]>([]);
  const statusesRef = useRef(initial);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const stop = useCallback(() => {
    clearTimer();
    watchingRef.current = [];
    if (mounted.current) setWatching([]);
  }, []);

  const tick = useCallback(async () => {
    if (!mounted.current) return;
    const slugs = watchingRef.current;
    if (!slugs.length) return;
    const next = { ...statusesRef.current };
    await Promise.all(
      slugs.map(async (slug) => {
        try {
          const { status } = await ConnectorsAPI.status(slug);
          next[slug] = status;
        } catch {
          // Transient; keep the last known status until the deadline.
        }
      })
    );
    if (!mounted.current) return;
    statusesRef.current = next;
    setStatuses(next);
    const still = slugs.filter((s) => next[s] !== "connected");
    watchingRef.current = still;
    setWatching(still);
    if (!still.length) {
      clearTimer();
      optionsRef.current.onAllConnected?.(next);
      return;
    }
    if (Date.now() - startedAt.current >= POLL_MAX_MS) {
      clearTimer();
      watchingRef.current = [];
      setWatching([]);
      setTimedOut(true);
      optionsRef.current.onTimeout?.(next);
      return;
    }
    timer.current = setTimeout(() => void tick(), POLL_INTERVAL_MS);
  }, []);

  const watch = useCallback(
    (slug: string) => {
      setTimedOut(false);
      if (!watchingRef.current.includes(slug)) {
        watchingRef.current = [...watchingRef.current, slug];
        setWatching(watchingRef.current);
      }
      if (!timer.current) {
        startedAt.current = Date.now();
        timer.current = setTimeout(() => void tick(), POLL_INTERVAL_MS);
      }
    },
    [tick]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimer();
    };
  }, []);

  return { statuses, watching, watch, stop, timedOut };
}
